import { useCallback, useState } from 'react';

import type { ColumnDef } from './DataTable';

const STORAGE_PREFIX = 'jotbill-columns-';

interface ColumnPreferences {
  visibleKeys: string[];
  orderedColumns: ColumnDef[];
}

function readPreferences(storageKey: string, defaultColumns: ColumnDef[]): ColumnPreferences {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as { visibleKeys: string[]; order: string[] };
      // Reorder defaultColumns based on stored order, keeping any new columns at the end
      const ordered = [
        ...parsed.order
          .map(key => defaultColumns.find(c => c.key === key))
          .filter((c): c is ColumnDef => c != null),
        ...defaultColumns.filter(c => !parsed.order.includes(c.key)),
      ];
      return { visibleKeys: parsed.visibleKeys, orderedColumns: ordered };
    }
  } catch {
    // ignore
  }
  return {
    visibleKeys: defaultColumns.map(c => c.key),
    orderedColumns: defaultColumns,
  };
}

export function useColumnPreferences(tabKey: string, defaultColumns: ColumnDef[]) {
  const storageKey = `${STORAGE_PREFIX}${tabKey}`;

  const [state, setState] = useState<ColumnPreferences & { storageKey: string }>(() => ({
    storageKey,
    ...readPreferences(storageKey, defaultColumns),
  }));

  /* A caller whose key changes with the view (the Claims queues, GDN-3381)
     is asking for THAT view's columns, not the last one's. React's own
     "adjust state during render" pattern: set here, and this render already
     shows the right set, with no flash of the previous view's columns and no
     effect round trip. Callers with a constant key never take this branch. */
  if (state.storageKey !== storageKey) {
    setState({ storageKey, ...readPreferences(storageKey, defaultColumns) });
  }

  const updatePreferences = useCallback(
    (visibleKeys: string[], orderedColumns: ColumnDef[]) => {
      setState({ storageKey, visibleKeys, orderedColumns });
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            visibleKeys,
            order: orderedColumns.map(c => c.key),
          }),
        );
      } catch {
        // ignore storage errors
      }
    },
    [storageKey],
  );

  const visibleColumns = state.orderedColumns.filter(c => state.visibleKeys.includes(c.key));

  return {
    allColumns: state.orderedColumns,
    visibleKeys: state.visibleKeys,
    visibleColumns,
    updatePreferences,
  };
}
